import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { scrollToTop } from '../../Utility/Utility'

import {StepOne}   from './Steps/StepOne'
import {StepTwo}   from './Steps/StepTwo'
import {StepThree} from './Steps/StepThree'
import {Section}   from '../../02-Molecules/Section/Section'

import './SectionBankDetails.scss'

const StepFour = (props) => {
  return (
    <div>
      <div className={'b-form-row'}>
        <div dangerouslySetInnerHTML={{ __html: props.text }} />
      </div>
      <div>
        <button className={'b-button-text-blue'} onClick={() => props.onBack(0)}>back</button>
      </div>
    </div>
  )
}

const PaymentComplete = (props) => {
  return (
    <div>
      Your payment is complete.
      For security your card details have not been stored
    </div>
  )
}

const ScrollToConfirmation = () => {
  const target = document.getElementById('confirm');
  target.style.visibility = "visible";
  const targetPosition = target.getBoundingClientRect().top + window.scrollY - 130;
  window.scrollTo({
    top: targetPosition,
    behavior: "smooth",
  });
}

/**
 * SectionBankDetails component
 */
export const SectionBankDetails = forwardRef(({ giftaidRate, loqateKey, collapsed, heading, sectionId, staticText, onCanProgressSet, paymentDates, tracking, trackEvent, validationHash, values, ...props }, ref) => {
  const parent = useRef()
  const [currentStep, currentStepSet]                 = useState(0)
  const [paymentHasCompleted, paymentHasCompletedSet] = useState(false)
  const [paymentError, paymentErrorSet]               = useState('')

  const [internalValues, internalValuesSet] = useState({
    account_owner    : '',
    account_holder   : '',
    account_number   : '',
    account_sort_code: '',
  })

  /**
   * Scroll the error message element into view.
   */
  const errorScrollIntoView = () => {
    const errorMessage = document.body.querySelector('.o-section-bank-details__payment-error')
    const yOffset = -230;
    const errorMessagePosition = errorMessage.getBoundingClientRect().top + window.scrollY + yOffset;

    if (errorMessage) {
      window.scrollTo({top: errorMessagePosition, behavior: 'smooth'});
    }
  }

  /**
   * Handle the external error.
   *
   * @param {string} message
   *   The message to set.
   */
  const handleExternalError = message => {
    paymentErrorSet(message)
    currentStepSet(1)
    errorScrollIntoView()
  }

  /**
   * Handle any form changes.
   *
   * @param {string} name
   *   The name of the value that changed
   * @param {string} value
   *   The new value
   */
  const handlerInputChange = (name, value) => {
    const internalValueFields = ['account_owner', 'account_holder', 'account_number', 'account_sort_code']

    if (internalValueFields.includes(name)) {
      internalValues[name] = value
      internalValuesSet(JSON.parse(JSON.stringify(internalValues)))
    }
    else {
      props.onInputChange(name, value)
    }

    if (paymentError) {
      paymentErrorSet('')
    }
  }

  /**
   * Jump to the given step.
   *
   * @param {number} step
   *   The step to jump to.
   */
  const handlerJumpToStep = step => {
    currentStepSet(step)

    if (step === 0) {
      handlerInputChange('account_owner', '')
    }
  }

  /**
   * Handle the payment.
   */
  const handlerPaymentDo = () => {
    return new Promise((resolve, reject) => {
      if (paymentHasCompleted) {
        resolve(true)
      }
      else if (currentStep === 2 && isValid()) {
        currentStepSet(4)
        resolve({
          account_holder   : internalValues.account_holder,
          account_number   : internalValues.account_number,
          account_sort_code: internalValues.account_sort_code,
          payment_provider : 'RSM',
          payment_method   : 'Direct debit',
        })
      }
      else if (currentStep === 1 && isValid()) {
        currentStepSet(2)
        reject()
      }
      else {
        reject()
      }
    })
  }

  /**
   * Determine if the component is in a valid state.
   */
  const isValid = () => {
    return (
      internalValues.account_owner     !== '' &&
      internalValues.account_holder    !== '' &&
      internalValues.account_number    !== '' &&
      internalValues.account_sort_code !== '' &&
      ((values.billing_address_line_1 !== '' &&
      values.billing_address_town_city !== '' &&
      values.billing_address_post_code !== '') || values.use_contact_address)
    )
  }

  /**
   * Update the order.
   *
   * @param {string} orderID
   *   The order ID.
   * @param {string} uuid
   *   The UUID.
   * @param {string} csrf_token
   *   The CSRF token.
   */
  const orderUpdate = (orderID, uuid, csrf_token) => {
    props.onInputChange('order_id', orderID)
    props.onInputChange('uuid', uuid)
    props.onInputChange('csrf_token', csrf_token)
  }

  useImperativeHandle(
    ref,
    () => ({
      doAction: () => handlerPaymentDo(),
      isValid : () => isValid(),
      setError: message => handleExternalError(message),
    }),
  )

  useEffect(() => {
    if (currentStep === 3) {
      onCanProgressSet(false)
    }
    else {
      onCanProgressSet(true)
    }

    if (currentStep === 2) {
      ScrollToConfirmation()
    }

  }, [currentStep])


  useEffect(() => {
    if (currentStep === 0 && internalValues.account_owner === 'y') {
      currentStepSet(1)
    }
    else if (currentStep === 0 && internalValues.account_owner === 'n') {
      currentStepSet(3)
    }

  }, [JSON.stringify(internalValues)])

  return (
    <Section tracking={tracking} trackEvent={trackEvent} collapsed={collapsed} heading={heading} sectionId={sectionId}>
      <div ref={parent}>
        {paymentError &&
          <div className={'b-form-row'} role="alert">
            <div className={'o-section-bank-details__payment-error'}>
              {paymentError}
            </div>
          </div>
        }

        <div className={'o-section-bank-details__wrapper'} data-error={paymentError !== ''}>
          {currentStep === 0 &&
          <StepOne
            fieldText={staticText.bank_details.account_holder_question_text}
            onInputChange={handlerInputChange}
            validationHash={validationHash}
            value={internalValues.account_owner}
          />
          }

          {<h2 id="confirm">Confirm bank details</h2>}

          {currentStep === 1 &&
          <StepTwo
            accountHolder={internalValues.account_holder}
            accountNumber={internalValues.account_number}
            accountSortCode={internalValues.account_sort_code}
            billingAddressLine1={values.billing_address_line_1}
            billingAddressLine2={values.billing_address_line_2}
            billingAddressTownCity={values.billing_address_town_city}
            billingAddressPostCode={values.billing_address_post_code}
            useContactAddress={values.use_contact_address}
            onInputChange={handlerInputChange}
            loqateKey={loqateKey}
            paymentStartDate={values.payment_start_date}
            paymentDates={paymentDates}
            validationHash={validationHash}
            onBack={handlerJumpToStep}
          />
          }

          {currentStep === 2 &&
          <StepThree
            accountHolder={internalValues.account_holder}
            accountNumber={internalValues.account_number}
            accountSortCode={internalValues.account_sort_code}
            recapText={staticText.bank_details.recap_text}
            guaranteeText={staticText.bank_details.guarantee}
            giftaidRate={giftaidRate}
            values={values}
            onBack={handlerJumpToStep}
          />
          }

          {currentStep === 3 && <StepFour text={staticText.bank_details.non_account_holder_text}  onBack={handlerJumpToStep} />}
          {currentStep === 4 && <PaymentComplete />}
        </div>

        <div className={'o-section-bank-details__logo'}>
          <span />
        </div>
      </div>
    </Section>
  )
})

/**
 * SectionBankDetails PropType
 */
SectionBankDetails.propTypes = {
  giftaidRate        :PropTypes.number,
  loqateKey          : PropTypes.string,
  collapsed          : PropTypes.bool,
  paymentHasCompleted: PropTypes.bool,
  paymentError       : PropTypes.bool,
  paymentDates       : PropTypes.array,
  braintreeSecret    : PropTypes.string,
  heading            : PropTypes.string,
  sectionId          : PropTypes.string,
  validationHash     : PropTypes.string,
  onInputChange      : PropTypes.func,
  onCanProgressSet   : PropTypes.func,
  staticText         : PropTypes.shape({
    bank_details: PropTypes.shape({
      account_holder_question_text: PropTypes.string,
      non_account_holder_text     : PropTypes.string,
      guarantee                   : PropTypes.string,
      recap_text                  : PropTypes.string,
      text_before                 : PropTypes.string,
      text_after                  : PropTypes.string,
    })
  }),
  tracking: PropTypes.shape({
    id  : PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func,
  values: PropTypes.shape({
    payment_amount           : PropTypes.string,
    payment_start_date       : PropTypes.string,
    first_name               : PropTypes.string,
    last_name                : PropTypes.string,
    email_address            : PropTypes.string,
    telephone_number         : PropTypes.string,
    use_contact_address      : PropTypes.bool,
    contact_address_line_1   : PropTypes.string,
    contact_address_line_2   : PropTypes.string,
    contact_address_town_city: PropTypes.string,
    contact_address_post_code: PropTypes.string,
    billing_address_line_1   : PropTypes.string,
    billing_address_line_2   : PropTypes.string,
    billing_address_town_city: PropTypes.string,
    billing_address_post_code: PropTypes.string,
    order_id                 : PropTypes.string,
    uuid                     : PropTypes.string,
  })
}
